import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import type { ZonePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

const zoneTypeLabels: Record<string, string> = {
  field: "Field",
  spectator: "Spectator",
  parking: "Parking",
  facility: "Facility",
};

export default function ZonesPage() {
  const schoolIds = useSchoolFilterIds();
  const [zones, setZones] = useState<ZonePublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ZonePublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const effectiveSchoolId = schoolIds[0] ?? "";

  useEffect(() => {
    if (schoolIds.length === 0) { setZones([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    Promise.all(schoolIds.map((id) => api.getZones(id).then((r) => r.zones)))
      .then((results) => setZones(results.flat()))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load zones"))
      .finally(() => setLoading(false));
  }, [schoolIds.join(","), location.key]);

  async function handleDelete(zoneId: string) {
    if (!confirm("Are you sure you want to delete this zone?")) return;
    try {
      await api.deleteZone(zoneId);
      setZones((prev) => prev.filter((z) => z.id !== zoneId));
      if (selected?.id === zoneId) setSelected(null);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete zone");
    }
  }

  return (
    <div className="flex h-full gap-0" data-page="zones-list" data-state={loading ? "loading" : error ? "error" : zones.length === 0 ? "empty" : "ready"}>
    <div className="min-w-0 flex-1 flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Zones</h1>
          <p className="text-muted-foreground">Manage campus zones for coach location broadcasting</p>
        </div>
        <div className="flex gap-2">
          {effectiveSchoolId && (
            <Link to={`/schools/${effectiveSchoolId}/zones/new`} className={buttonVariants({ size: "sm" })} data-action="create">
              <Plus className="mr-2 h-4 w-4" />
              Add Zone
            </Link>
          )}
          <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned(p => !p)} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <SchoolFilter />
      </div>

      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {!loading && !error && zones.length === 0 && effectiveSchoolId && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <p className="text-muted-foreground">No zones yet</p>
          <Link
            to={`/schools/${effectiveSchoolId}/zones/new`}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            Add your first zone
          </Link>
        </div>
      )}

      {!loading && zones.length > 0 && (
        <div className="overflow-x-auto" data-region="results">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>GPS</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones.map((zone) => (
                <TableRow
                  key={zone.id}
                  className={`cursor-pointer ${selected?.id === zone.id ? "bg-muted" : ""}`}
                  onClick={() => setSelected(zone)}
                  onDoubleClick={() => navigate(`/zones/${zone.id}/edit`)}
                  data-item="zone"
                  data-item-id={zone.id}
                >
                  <TableCell className="font-medium">{zone.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {zoneTypeLabels[zone.zone_type] ?? zone.zone_type}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {zone.latitude != null && zone.longitude != null
                      ? `${zone.latitude.toFixed(5)}, ${zone.longitude.toFixed(5)}`
                      : "--"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {zone._actions.includes("edit") && (
                        <Link to={`/zones/${zone.id}/edit`}>
                          <Button variant="ghost" size="sm" data-action="edit">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                      {zone._actions.includes("delete") && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(zone.id); }} data-action="delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
    <PreviewPane pinned={pinned} onTogglePin={() => setPinned(p => !p)} title="Zone">
      {selected && (
        <>
          <PreviewField label="Name">{selected.name}</PreviewField>
          <PreviewField label="Type">{zoneTypeLabels[selected.zone_type] ?? selected.zone_type}</PreviewField>
          <PreviewField label="GPS">
            {selected.latitude != null && selected.longitude != null
              ? `${selected.latitude.toFixed(5)}, ${selected.longitude.toFixed(5)}`
              : "--"}
          </PreviewField>
        </>
      )}
    </PreviewPane>
    </div>
  );
}
